/**
 * Spirograph geometry.
 *
 * A wheel of radius r rolling inside a fixed ring of radius R, with a pen at distance
 * d from the wheel's centre, traces a hypotrochoid. Everything on the page derives
 * from that one curve plus the gear outlines that explain it.
 */

/** Greatest common divisor, for working out when a curve closes. */
const gcd = (a, b) => (b ? gcd(b, a % b) : a);

/**
 * Carrier revolutions before the pattern repeats.
 *
 * With N ring teeth and n wheel teeth the curve closes after n / gcd(N, n)
 * revolutions and draws N / gcd(N, n) petals — 96/52 closes after 13 revolutions with
 * 24 petals. This is what the scroll length is set from: the drawing should finish
 * as the reader reaches the bottom, not a third of the way down or never.
 */
export const closureRevolutions = (N, n) => n / gcd(N, n);
export const petalCount = (N, n) => N / gcd(N, n);

/**
 * Where the wheel's own rotation has got to, in radians.
 *
 * Negative because the wheel rolls *inside* the ring: as the carrier advances
 * anticlockwise the wheel spins clockwise about its own centre, at (R − r) / r times
 * the carrier rate.
 */
export const wheelRotation = (R, r, theta) => -theta * ((R - r) / r);

/** Pen position in world coordinates, origin at the ring's centre. */
export const penPoint = (R, r, d, phase, theta) => {
  const a = wheelRotation(R, r, theta) + phase;
  return [(R - r) * Math.cos(theta) + d * Math.cos(a), (R - r) * Math.sin(theta) + d * Math.sin(a)];
};

/** Centre of the rolling wheel at a given carrier angle. */
export const wheelCentre = (R, r, theta) => [
  (R - r) * Math.cos(theta),
  (R - r) * Math.sin(theta),
];

/**
 * Five inks, cycled by clicking a pen.
 *
 * Brighter and more saturated than the essays' categorical palette on purpose: these
 * are hairlines on black rather than filled areas, and a muted ink at one pixel wide
 * disappears into the background.
 */
export const PALETTE = ['#f4b942', '#e8604c', '#4fb8d8', '#8ac44f', '#b57edc'];

/**
 * Gear outline as SVG path data, after Mike Bostock's epicyclic gearing.
 *
 * Kept as a path string rather than drawn imperatively so it can be handed straight
 * to `new Path2D(...)` once and re-stroked under a transform every frame, instead of
 * rebuilding a few hundred arc segments per gear per frame.
 *
 * `annulus` turns the gear inside out: the teeth point inward and the body becomes
 * the material outside them, which is what a ring gear is.
 */
export const gearPath = ({ teeth, radius, annulus, toothRadius, holeRadius }) => {
  const n = teeth;
  let r2 = Math.abs(radius);
  let r0 = r2 - toothRadius;
  let r1 = r2 + toothRadius;
  let r3 = holeRadius;
  if (annulus) {
    r3 = r0;
    r0 = r1;
    r1 = r3;
    r3 = r2 + toothRadius * 3;
  }
  const da = Math.PI / n;
  let a0 = -Math.PI / 2 + (annulus ? Math.PI / n : 0);
  const path = ['M', r0 * Math.cos(a0), ',', r0 * Math.sin(a0)];
  let i = -1;
  while (++i < n) {
    path.push(
      'A', r0, ',', r0, ' 0 0,1 ', r0 * Math.cos((a0 += da)), ',', r0 * Math.sin(a0),
      'L', r2 * Math.cos(a0), ',', r2 * Math.sin(a0),
      'L', r1 * Math.cos((a0 += da / 3)), ',', r1 * Math.sin(a0),
      'A', r1, ',', r1, ' 0 0,1 ', r1 * Math.cos((a0 += da / 3)), ',', r1 * Math.sin(a0),
      'L', r2 * Math.cos((a0 += da / 3)), ',', r2 * Math.sin(a0),
      'L', r0 * Math.cos(a0), ',', r0 * Math.sin(a0)
    );
  }
  path.push('M0,', -r3, 'A', r3, ',', r3, ' 0 0,0 0,', r3, 'A', r3, ',', r3, ' 0 0,0 0,', -r3, 'Z');
  return path.join('');
};
